/**
 * Smart Sticky: bidirectional sticky behavior for multiple elements.
 *
 * When the element is shorter than the viewport → classic sticky top.
 * When taller:
 *   - Scrolling DOWN → the element scrolls naturally until its bottom
 *     reaches the viewport bottom, then sticks there.
 *   - Scrolling UP → the element scrolls naturally until its top
 *     reaches the viewport top, then sticks there.
 *
 * Works by toggling between position:sticky with dynamic top values
 * and position:relative with a computed translateY when "free-scrolling".
 *
 * Usage: add [data-smart-sticky] to each target element,
 *        add [data-smart-sticky-wrapper] to their shared scroll-boundary parent.
 */
(function () {
  const SELECTOR = '[data-smart-sticky]';
  const WRAPPER_SELECTOR = '[data-smart-sticky-wrapper]';
  const DESKTOP_MIN = 990;
  const GAP = 24;

  const instances = [];
  let lastScrollY = window.scrollY;
  let direction = 'down';
  let ticking = false;

  /**
   * Get the visible header height (sticky header offset).
   * Reads --header-height from document.body where header.js sets it dynamically.
   * Returns only GAP when the floating header is hidden (header-hidden class on body).
   */
  const getTopOffset = () => {
    if (document.body.classList.contains('header-hidden')) return GAP;
    const val = getComputedStyle(document.body).getPropertyValue('--header-height');
    const headerHeight = val ? parseInt(val, 10) || 0 : 0;
    return headerHeight + GAP;
  };

  class SmartSticky {
    #el;
    #wrapper;
    #mode = 'top';
    #freeOffset = 0;
    #resizeObserver = null;
    #lastHeight = 0;

    constructor(el) {
      this.#el = el;
      this.#wrapper = el.closest(WRAPPER_SELECTOR) || el.parentElement;
      this.#lastHeight = el.offsetHeight;
      this.#observeResize();
      this.#listenToggle();
    }

    /** Called on every scroll frame by the shared handler. */
    apply() {
      if (window.innerWidth < DESKTOP_MIN) {
        this.#reset();
        return;
      }

      const topOffset = getTopOffset();
      const viewportH = window.innerHeight;
      const elH = this.#el.offsetHeight;

      // If element fits in viewport, use classic sticky top
      if (elH + topOffset <= viewportH) {
        this.#setSticky(topOffset);
        this.#mode = 'top';
        return;
      }

      const wrapperRect = this.#wrapper.getBoundingClientRect();
      const elRect = this.#el.getBoundingClientRect();
      const elTopInWrapper = elRect.top - wrapperRect.top;

      if (direction === 'down') {
        if (this.#mode === 'top') {
          this.#setFree(elTopInWrapper);
        }

        if (this.#mode === 'free' && elRect.bottom <= viewportH) {
          this.#setSticky(viewportH - elH);
          this.#mode = 'bottom';
        }
      } else {
        if (this.#mode === 'bottom') {
          this.#setFree(elTopInWrapper);
        }

        if (this.#mode === 'free' && elRect.top >= topOffset) {
          this.#setSticky(topOffset);
          this.#mode = 'top';
        }
      }
    }

    /** Reset state after a resize so the next apply() starts clean. */
    resetState() {
      this.#mode = 'top';
      this.#freeOffset = 0;
    }

    #setSticky(top) {
      this.#el.style.position = 'sticky';
      this.#el.style.top = `${top}px`;
      this.#el.style.transform = '';
    }

    #setFree(offset) {
      this.#freeOffset = offset;
      this.#el.style.position = 'relative';
      this.#el.style.top = '';
      this.#el.style.transform = `translateY(${this.#freeOffset}px)`;
      this.#mode = 'free';
    }

    #reset() {
      this.#el.style.position = '';
      this.#el.style.top = '';
      this.#el.style.transform = '';
      this.#mode = 'top';
      this.#freeOffset = 0;
    }

    /**
     * Called when the element's height changes (e.g. accordion open/close).
     * Re-evaluates sticky mode while trying to keep visual continuity.
     */
    #onHeightChange() {
      if (window.innerWidth < DESKTOP_MIN) return;

      const topOffset = getTopOffset();
      const viewportH = window.innerHeight;
      const elH = this.#el.offsetHeight;

      if (elH + topOffset <= viewportH) {
        this.#setSticky(topOffset);
        this.#mode = 'top';
        return;
      }

      if (this.#mode === 'bottom') {
        this.#setSticky(viewportH - elH);
        return;
      }

      if (this.#mode === 'free') {
        const elRect = this.#el.getBoundingClientRect();
        if (elRect.bottom > viewportH && direction === 'down') {
          this.#setSticky(viewportH - elH);
          this.#mode = 'bottom';
        }
      }
    }

    #observeResize() {
      if (typeof ResizeObserver === 'undefined') return;

      this.#resizeObserver = new ResizeObserver((entries) => {
        const newHeight = entries[0].contentRect.height;
        if (newHeight !== this.#lastHeight) {
          this.#lastHeight = newHeight;
          this.#onHeightChange();
        }
      });
      this.#resizeObserver.observe(this.#el);
    }

    #listenToggle() {
      this.#el.addEventListener(
        'toggle',
        () => requestAnimationFrame(() => this.#onHeightChange()),
        true
      );
    }
  }

  // ── Shared event handlers ──────────────────────────────────────────

  function onScroll() {
    if (ticking) return;
    ticking = true;

    requestAnimationFrame(() => {
      const scrollY = window.scrollY;
      const delta = scrollY - lastScrollY;

      if (delta > 0) direction = 'down';
      else if (delta < 0) direction = 'up';

      for (const instance of instances) {
        instance.apply();
      }

      lastScrollY = scrollY;
      ticking = false;
    });
  }

  function onResize() {
    lastScrollY = window.scrollY;

    for (const instance of instances) {
      instance.resetState();
      instance.apply();
    }
  }

  // ── Init ───────────────────────────────────────────────────────────

  function init() {
    for (const el of document.querySelectorAll(SELECTOR)) {
      instances.push(new SmartSticky(el));
    }

    if (instances.length === 0) return;

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
